import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoAdd,
  IoAlertCircleOutline,
  IoSearchOutline,
  IoWarningOutline,
  IoChevronForward,
  IoStorefrontOutline,
  IoCheckboxOutline,
  IoClose,
  IoCheckmarkDone,
  IoTrashOutline,
} from "react-icons/io5";
import { toast } from "sonner";
import api, { fileUrl } from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { relativeTime } from "@/utils";

export default function ProblemsListPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/problems");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (statusFilter === "active" && p.status === "resolved") return false;
      if (priorityFilter && p.priority !== priorityFilter) return false;
      if (q) {
        const s = (p.title + " " + (p.description || "") + " " + p.store_id).toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, statusFilter, priorityFilter, q]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    await api.post("/problems/bulk_delete", { ids: [...selected] });
    toast.success("Eliminati");
    exitSelectMode();
    load();
  };

  const withBar = selectMode && selected.size > 0;

  return (
    <div className={`mobile-frame ${withBar ? "with-bottom-bar" : ""}`} data-testid="problems-page">
      {selectMode ? (
        <div className="tf-header-simple">
          <button data-testid="select-cancel" className="tf-close-btn" onClick={exitSelectMode}>
            <IoClose size={22} />
          </button>
          <div className="tf-header-title-center" data-testid="page-title">
            {selected.size === 0
              ? t("select_all")
              : selected.size === 1
              ? t("selected_count_1")
              : t("selected_count_n").replace("$", selected.size)}
          </div>
          <button data-testid="select-all-btn" className="tf-icon-btn ghost" onClick={selectAll}>
            <IoCheckmarkDone size={20} color="#E2001A" />
          </button>
        </div>
      ) : (
        <AppHeader
          title={t("problems")}
          showBack
          backTo="/"
          right={
            <button
              data-testid="header-select-btn"
              onClick={() => { setSelectMode(true); setSelected(new Set()); }}
              className="tf-icon-btn ghost"
              aria-label={t("select_all")}
            >
              <IoCheckboxOutline size={20} />
            </button>
          }
        />
      )}

      <div className="tf-content">
        <div className="tf-field" style={{ marginBottom: 12 }}>
          <div className="tf-input-wrap">
            <IoSearchOutline size={20} className="tf-input-icon-left" />
            <input
              data-testid="problems-search"
              className="tf-input tf-input-with-icon-left"
              placeholder={t("search_problem")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="tf-chips-row">
          <button data-testid="chip-active" className={`tf-chip ${statusFilter === "active" ? "active-red" : ""}`} onClick={() => setStatusFilter("active")}>
            {t("filter_active")}
          </button>
          <button data-testid="chip-all" className={`tf-chip ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
            {t("filter_all")}
          </button>
        </div>

        <div className="tf-chips-row" style={{ paddingTop: 0, marginTop: -4 }}>
          <button data-testid="chip-priority-high" className={`tf-chip ${priorityFilter === "high" ? "active" : ""}`} onClick={() => setPriorityFilter(priorityFilter === "high" ? "" : "high")}>
            <span className="tf-dot red" />{t("filter_priority_high")}
          </button>
          <button data-testid="chip-priority-medium" className={`tf-chip ${priorityFilter === "medium" ? "active" : ""}`} onClick={() => setPriorityFilter(priorityFilter === "medium" ? "" : "medium")}>
            <span className="tf-dot" style={{ backgroundColor: "#F59E0B" }} />{t("filter_priority_medium")}
          </button>
          <button data-testid="chip-priority-low" className={`tf-chip ${priorityFilter === "low" ? "active" : ""}`} onClick={() => setPriorityFilter(priorityFilter === "low" ? "" : "low")}>
            <span className="tf-dot blue" />{t("filter_priority_low")}
          </button>
        </div>

        {loading ? (
          <div className="tf-empty">{t("loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="tf-empty">
            <IoAlertCircleOutline size={44} />
            <div>{t("no_problems")}</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((p) => {
              const isSelected = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  data-testid={`problem-item-${p.id}`}
                  onClick={() => selectMode ? toggle(p.id) : navigate(`/problem/${p.id}`)}
                  className="tf-problem-item"
                  style={{
                    backgroundColor: isSelected ? "var(--tf-red-soft)" : "var(--tf-surface)",
                    border: `2px solid ${isSelected ? "var(--tf-red)" : "var(--tf-text)"}`,
                    borderRadius: 18,
                    padding: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    cursor: "pointer",
                    position: "relative",
                    color: "var(--tf-text)",
                  }}
                >
                  {selectMode && (
                    <div style={{
                      position: "absolute", top: 10, right: 10,
                      width: 26, height: 26, borderRadius: 999,
                      border: `2px solid ${isSelected ? "var(--tf-red)" : "var(--tf-border)"}`,
                      backgroundColor: isSelected ? "var(--tf-red)" : "var(--tf-surface)",
                      color: "white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isSelected && <IoCheckmarkDone size={14} />}
                    </div>
                  )}
                  <div style={{
                    width: 64, height: 64, borderRadius: 14,
                    backgroundColor: p.photo_paths?.[0] ? "var(--tf-input-bg)" : "var(--tf-red-soft)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--tf-red)", overflow: "hidden", flexShrink: 0,
                  }}>
                    {p.photo_paths?.[0] ? (
                      <img src={fileUrl(p.photo_paths[0])} alt="" loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <IoWarningOutline size={28} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="tf-row" style={{ gap: 6, flexWrap: "wrap" }}>
                      <span className={`tf-priority tf-priority-${p.priority}`}>
                        {p.priority === "high" ? t("priority_high") : p.priority === "medium" ? t("priority_medium") : t("priority_low")}
                      </span>
                      <span className={`tf-status tf-status-${p.status}`} data-testid={`problem-status-${p.id}`}>
                        {t(`status_${p.status}`)}
                      </span>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 16, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.title}
                    </div>
                    <div className="tf-small tf-row" style={{ marginTop: 4, gap: 6 }}>
                      <IoStorefrontOutline size={13} />
                      {t(STORE_LABELS[p.store_id])} · {relativeTime(p.created_at, lang)}
                    </div>
                  </div>
                  {!selectMode && <IoChevronForward size={20} color="var(--tf-muted)" style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!selectMode && (
        <button data-testid="fab-add-problem" className="tf-fab" onClick={() => navigate("/problem/new")}>
          <IoAdd size={22} />
          {t("add_problem")}
        </button>
      )}

      {withBar && (
        <div className="tf-select-bottom-bar" data-testid="select-bottom-bar" style={{ gridTemplateColumns: "1fr" }}>
          <button data-testid="bulk-delete-btn" onClick={() => setConfirmOpen(true)}>
            <IoTrashOutline size={22} />
            {t("delete")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={bulkDelete}
        title={t("confirm_delete")}
        description={selected.size === 1 ? t("selected_count_1") : t("selected_count_n").replace("$", selected.size)}
        confirmLabel={t("delete")}
      />
    </div>
  );
}
